import { useEffect, useRef, useState } from 'react';

/*
 * Tracks whether an element is on screen, so callers can mount an expensive
 * child (a live video player) only while it's actually visible.
 *
 * This exists because the event lists don't virtualize: infinite scroll keeps
 * every loaded page in the DOM, so without this every live card ever scrolled
 * past keeps an HLS player buffering and decoding. Dozens of those at once is
 * what pushed Chrome into multi-GB territory on the testers' machines.
 *
 * `margin` starts the player slightly before it scrolls into view so there's no
 * visible gap; the element keeps rendering its poster/thumbnail while off screen.
 */
export const useInViewport = <T extends HTMLElement>(
  margin = '200px'
): [React.RefObject<T>, boolean] => {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return undefined;
    }

    // Safari < 12.1 and jsdom have no IntersectionObserver; play it safe there.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);

      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
      },
      { rootMargin: margin }
    );

    observer.observe(element);

    return (): void => {
      observer.disconnect();
    };
  }, [margin]);

  return [ref, visible];
};
